'use client';

import { equals, keys } from 'ramda';
import {
  type PropsWithChildren,
  type ReactElement,
  createContext,
} from 'react';

import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import { usePollingEffect } from '@/hooks/use-polling-effect';
import { type AbTastyTestsOnPage } from '@/lib/ab-tasty';

type Props = PropsWithChildren;

export const AbTastyContext = createContext<AbTastyTestsOnPage | null>(null);

export const AbTastyProvider = (props: Props): ReactElement => {
  const { children } = props;

  const { isEnabled } = useFeatureToggle();
  if (!isEnabled('ab-tasty')) {
    return <>{children}</>;
  }

  // When the page changes, AB Tasty reports about tests after a delay.
  // To work around that, we monitor the value of getTestsOnPage().
  // eslint-disable-next-line react-hooks/rules-of-hooks -- Condition is project-wide
  const abTastyTests = usePollingEffect<AbTastyTestsOnPage | undefined>(
    () => {
      if (typeof window === 'undefined') return;
      return window.ABTasty?.getTestsOnPage?.();
    },
    (a, b) => equals(keys(a), keys(b)),
    1000
  );

  return (
    <AbTastyContext.Provider value={abTastyTests ?? null}>
      {children}
    </AbTastyContext.Provider>
  );
};
